import { IDeviceTableNode } from './IDeviceTable';
import { getPoolConnectClass } from '../ECoSystemsHelper';
import { Pool, PoolAdditionalData } from '../../../apis/pool.api';
import { EventProductFunctionnalityEnum } from '../../../apis/poolEvent.api';

export enum IProductFunctionnality {
  COUVERTURE,
  ECLAIRAGE,
  FILTRATION_STANDARD,
  LINK,
  SENSE,
  INJECTION_CHLORE,
  ELECTROLYSEUR_STANDARD = 6,
  INJECTION_PH = 7,
  ROBOT = 8,
  TAIR,
  PAC,
  NCC,
  BLOWER = 12,
  VOLET24 = 13,
  VOLET230 = 14,
  FILTRATION_VS = 15,
  ELECTROLYSEUR_SALT = 16,
  INJECTION_CLPH = 17,
  WATER_LEVEL = 18,
}

export enum ProductDependanceEnum {
  NO_DEPENDANCE = 0,
  DEPENDANCE = 1,
  DEPENDANCE_NOTIFICATION = 2,
  IMPOSSIBLE = 9,
}

export type CPSConfigInstallation = {
  component: any;
  condition: (pool: Pool, poolAdditionalData: PoolAdditionalData) => boolean;
};

export type CPSInstallation = {
  pages: CPSConfigInstallation[];
  finish?: (
    pool: Pool,
    productClass: IProduct,
    editValue: any,
    getValue: any,
    additionalData: PoolAdditionalData,
  ) => boolean | void | undefined;
  reset?: (
    pool: Pool,
    productClass: IProduct,
    editValue: any,
    getValue?: any,
    additionalData?: PoolAdditionalData,
  ) => boolean | void | undefined;
};

export type CPSMiseEnService = {
  init?: (
    pool: Pool,
    productClass: IProduct,
    editValue: any,
    getValue: any,
    additionalData: PoolAdditionalData,
  ) => boolean;
  pages: CPSConfigInstallation[];
};

export type CPSConfigCard = {
  component: any;
  weight: number;
  productClass?: IProduct;
};

export type CPSConfigAlarm = {
  component: any;
  weight: number;
  productClass?: IProduct;
  alarms?: CPSConfigAlarmElement[];
};

export type CPSConfigAlarmElement = {
  productClass?: IProduct;
  functionality: number;
  functionalityTitle: string;
  title: string;
  ctaTitle?: string;
  ctaFn?: (editValue: any) => void;
};

export type ProductTutoSlide = {
  image: any;
  text: string;
};

export type ProductTuto = {
  hasHelp?: boolean;

  helpTitle?: string;
  helpSlides?: string[];

  slides?: ProductTutoSlide[];
};

export type CPSConfig = {
  installation?: CPSInstallation;

  statusCards?: CPSConfigCard[];
  controlCards?: CPSConfigCard[];

  alarms?: CPSConfigAlarm;

  parametrages: {
    enabledUser: boolean;
    enabledSupport: boolean;

    general: {
      enabledUser: boolean;
      enabledSupport: boolean;
      component?: any;
    };

    configuration?: {
      enabledUser: boolean;
      enabledSupport: boolean;
      component?: any;
    };
  };
};

export type OTAUpdateProperty = {
  index: number;
  propertyName: string;
};

export type IProductDependance = {
  modelIdentifier: string;
  level: number;
};

export enum HistoryGraphModeEnum {
  EVENT = 'event',
  DATA = 'data',
}

export enum HistoryGraphYModeEnum {
  HOUR = 'hour',
  SECONDE = 'second',
  LITRE = 'litre',
  WATT = 'watt',
  MVOLT = 'mvolt',
  VOLT = 'volt',
  VOLT_MILLIEME = 'volt_millieme',
  PH = 'ph',
  PH_CENTIEME = 'ph_centieme',
  TEMPERATURE = 'temperature',
}

export enum HistoryChartModeEnum {
  NORMAL = 'normal',
  LINEAR = 'linear',
  BAR = 'bar',
}

export type CPSConfigFunctionnalityGraphiques = {
  title?: string;
  mode?: HistoryGraphModeEnum;
  codeEvent?: number;
  yMode?: HistoryGraphYModeEnum;
  property?: string;
  yModeOptions?: {
    min?: number;
    max?: number;
    minMensuel?: number;
    maxMensuel?: number;
    step?: number;
    stepMensuel?: number;
    divided?: number;
  };
  aggregate?: string;
  valueToMatch?: number;
  chartMode?: HistoryChartModeEnum;
  enabledTotal?: boolean;
  modelIdentifier?: string;

  customGraph?: any;
  customGraphComponent?: any;

  showDirectly?: boolean;
};

export type CPSConfigFunctionnality = {
  miseEnOeuvre?: CPSMiseEnService;

  controle: {
    enabledUser: boolean;
    enabledSupport: boolean;

    component?: any;
  };

  parametrages: {
    enabledUser: boolean;
    enabledSupport: boolean;

    reglage: {
      enabledUser: boolean;
      enabledSupport: boolean;
      component?: any;
    };
  };
  status: {
    enabledUser: boolean;
    enabledSupport: boolean;
    component?: any;
  };

  historique: {
    enabledUser: boolean;
    enabledSupport: boolean;

    suivi: {
      enabledUser: boolean;
      enabledSupport: boolean;
      graphiques?: CPSConfigFunctionnalityGraphiques[];
    };
  };
};

export abstract class IProduct {
  id?: string;

  deviceTableIndex: number = 0;

  dataIdentifier?: string;

  modelIdentifier?: string;

  manufacturerName?: string;

  serialNumber?: string;

  firmwareVersion?: number;

  hardwareVersion?: string;

  alertEnabled?: boolean;

  adviceEnabled?: boolean;

  statusEnabled?: boolean;

  installationDate?: string;

  abstract data: any;

  abstract getCPSConfigByFonctionnality(
    functionnality: IProductFunctionnality | EventProductFunctionnalityEnum,
  ): CPSConfigFunctionnality | null;
  abstract getCPSConfig(): CPSConfig | null;

  abstract getProductKey(): string;
  abstract getProductSmallIllustration(): any;
  abstract getProductIllustration(): any;
  abstract getProductImage(): any;

  abstract getProductVideoURI(langCode: string): string | null;
  abstract getProductVideoChapterURI(langCode: string): string | null;

  abstract getProductTuto(): ProductTuto;
  abstract getUpdateTuto(): ProductTuto;

  abstract getProductDataFromPool(pool: Pool): any;
  abstract getProductDeviceTableFromPool(pool: Pool): any;

  abstract getEventProductFunctionalities(): EventProductFunctionnalityEnum[];

  hasUpdateFromConnect(pool: Pool): boolean {
    const dT = this.getProductDeviceTableFromPool(pool) as IDeviceTableNode;

    const hasConnect = getPoolConnectClass(pool) != null;

    if (
      hasConnect &&
      dT.firmwareVersion != dT.gblVersion &&
      dT.gblVersion! > 0
    ) {
      return true;
    }

    return false;
  }

  abstract getPropertyForOTAUpdate(): OTAUpdateProperty | null;
  abstract getMinimumFirmwareVersionForBleUpdate(): number | null;
  abstract needConfiguration(pool: Pool): boolean;
}
